import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader } from "@/components/v-pos/loader.component";
import { useGetVirtualPosByVirtualPosIdQuery } from "@/lib/services/data-layer/core";
import { FC, ReactNode, useState } from "react";
import { formatPersianDate, numberSeparator } from "@/lib/helper";
import { GoCheckCircleFill } from "react-icons/go";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { virtualPosItem } from "@/lib/types/DTO/core";

type StatusVPosLinkDialogProps = {
  linkId?: number;
  buttonTrigger?: (check?: boolean) => ReactNode;
  itemsDetails?: virtualPosItem;
    canOpen?: boolean;

};

const StatusVPosLinkDialog: FC<StatusVPosLinkDialogProps> = ({
  linkId,
  buttonTrigger,
  itemsDetails,
  canOpen = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const shouldFetch = dialogOpen && !itemsDetails?.isPaid;

  const { data, isLoading, error, refetch, isRefetching } =
    useGetVirtualPosByVirtualPosIdQuery(linkId, shouldFetch);

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);

    // Only refetch if:
    // 1. Dialog is opening
    // 2. linkId exists
    // 3. Status is not already true (paid)
    // 4. Data doesn't show it's already paid
    if (open && linkId && !itemsDetails?.isPaid && !data?.isPaid) {
      refetch();
    }
  };

  const loadingState = isRefetching || isLoading;

  const item = itemsDetails || data;

  return (
    <Dialog open={canOpen && dialogOpen} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger disabled={!!linkId} asChild className="rounded-2xl ">
        {buttonTrigger ? (
          buttonTrigger(itemsDetails?.isPaid || data?.isPaid)
        ) : (
          <Button
            type="button"
            className="w-full bg-[#F89521] hover:bg-[#F89521] font-bold rounded-2xl"
            disabled={!linkId}
          >
            استعلام وضعیت تراکنش
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[360px] p-0 gap-0 text-base rounded-2xl">
        <DialogHeader className="rounded-t-[6px] border-b py-[30px] pb-[10px]">
          <DialogTitle className="flex justify-center items-center font-bold text-lg">
            استعلام وضعیت تراکنش
          </DialogTitle>
          <DialogDescription className="hidden"></DialogDescription>
        </DialogHeader>

        {loadingState && (
          <div className="h-[267px]">
            <Loader />
          </div>
        )}

        {!loadingState && error && (
          <div className="flex justify-center items-center py-10">
            {error?.message}
          </div>
        )}

        {!loadingState && item && (
          <>
            <div
              className={`text-center flex flex-col mx-[16px] justify-center items-center mt-[20px] mb-[12px] ${
                item.isPaid ? "text-[#5B951D]" : "text-[#F89521]"
              }`}
            >
                {item.isPaid ? (
                <>
                  <GoCheckCircleFill className="w-[45px] h-[45px] flex-none" />
                  <p className="sm:mr-[10px] text-lg font-semibold mt-2">
                    تراکنش با موفقیت پرداخت شده است.
                  </p>
                </>
              ) : (
                <>
                  <BsFillQuestionCircleFill className="w-[45px] h-[45px] flex-none" />
                  <p className="sm:mr-[10px] text-lg font-semibold mt-2">
                    تراکنش هنوز پرداخت نشده است!
                  </p>
                </>
              )}
            </div>

            {/* Only show details if we have data from API */}
            {item && (
              <div className="w-full flex flex-col px-[8px] justify-center items-center">
                <p className="mb-[8px]">شماره موبایل:</p>
                <p className="font-semibold text-xl">{data?.mobile}</p>
                <p className="mt-[20px] mb-[8px] text-base">مبلغ پرداختی: </p>
                <p className="font-bold text-xl">
                  {numberSeparator({
                    value: String(item?.amount),
                    type: "money",
                  })}
                  <span className="mr-1 text-sm">ریال</span>
                </p>
                <p className="mt-[20px] mb-[8px]">تاریخ:</p>
                <p className="font-bold text-base">
                  {formatPersianDate({
                    dateString: item?.createdAt,
                    mode: "onlyDate",
                    format: "text",
                  })}
                </p>
                <p className="font-bold text-base">
                  {formatPersianDate({
                    dateString: item?.createdAt,
                    mode: "onlyTime",
                    format: "text",
                  })}
                </p>
              </div>
            )}
          </>
        )}

        <DialogClose asChild>
          <Button className="w-[calc(100%-12px)] mx-auto mb-[20px] font-bold mt-[45px] rounded-2xl">
            بـاشه!
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export default StatusVPosLinkDialog;
